import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import Modal from "components/modal/Modal";

import Submenu from "components/menu/Submenu";
import Header from "views/header/Header";
import Footer from "views/footer/Footer";
import Customers from "views/customers/Customers";
import Inbox from "views/inbox/containers/Inbox";
import Dashboard from "views/dashboard/containers/Dashboard";
import { Documents } from "views/customers/documents/containers/Documents";
import CustomerSettings from "views/customers/settings/containers/CustomerSettings";
import CustomerServerSettings from "views/settings/containers/CustomerServerSettings";
import DocumentDelivery from "views/delivery/containers/DocumentDelivery";
import DocumentArchive from "views/documents/containers/DocumentArchive";
import Wizard from "views/wizard/containers/Wizard";
import Admin from "views/admin/containers/Admin";
import Inventory from "views/inventory/containers/Inventory";
import Invoices from "views/invoices/containers/Invoices";
import { hasAccessToInvoiceReport, logoutUser } from "common/MetaActions";
import AllCustomers from "views/customers/allCustomers/containers/AllCustomers";
import MyCustomers from "views/customers/myCustomers/containers/MyCustomers";
import Thread from "views/inbox/components/Thread";
import MyThreads from "views/inbox/containers/myThreads/MyThreads";
import { AllThreads } from "views/inbox/containers/allThreads/AllThreads";
import SentMessageList from "views/inbox/containers/sentMessages/SentMessageList";
import MessageEditor from "views/inbox/components/MessageEditor/MessageEditor";
import { DocumentDeliveryUpload } from "views/delivery/containers/DocumentDeliveryUpload";
import { DocumentDeliveryTable } from "views/delivery/containers/DocumentDeliveryTable";
import { DocumentDeliveryTableWithOptions } from "views/delivery/containers/DocumentDeliveryTableWithOptions";
import CustomerSubmenu, {
  SubMenuItemType
} from "../components/CustomerSubmenu";

import "./Layout.scss";
import { useRootStore } from "store";
import { PrimaryButton } from "components/button/Button";

export default function Layout() {
  const {
    fetchCustomers,
    fetchCustomer,
    myCustomers,
    workingCustomer: customer
  } = useRootStore((state) => state.customers);
  const { unfilteredCustomers: customerList } = myCustomers;
  const location = useLocation();
  const { workingCustomerId } = useRootStore((state) => state.global);
  const { user, inventory } = useRootStore((state) => state);
  const { uploadedDocuments, isLoadingUploadedDocuments } = useRootStore(
    (state) => state.delivery
  );
  const { getInventories } = inventory;
  const { fetchDashboard } = useRootStore((state) => state.dashboard);
  const { getSettings } = useRootStore((state) => state.customerServerSettings);

  const { sentMessages, loadInbox } = useRootStore((state) => state.inbox);
  const { customerNumber } = customer;
  const isExternalUser = user.userInfo && user.userInfo.type === "External";
  const { sessionExpired, setSessionExpired } = user;

  useEffect(() => {
    if (isExternalUser) {
      getSettings();
    }
    if (
      user.isAuthenticated &&
      !customer.isLoading &&
      !customer.customerId &&
      customerList.length === 0
    ) {
      fetchCustomers(isExternalUser);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (workingCustomerId && customerNumber !== global.workingCustomerId) {
      fetchCustomer(workingCustomerId);
      fetchDashboard(workingCustomerId);
      if (isExternalUser) {
        getInventories(workingCustomerId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingCustomerId, isExternalUser, fetchCustomer, getInventories, fetchDashboard]);

  const showCustomerMenu = () => {
    return ![
      "/addCustomer",
      "/wizard",
      "/admin",
      "/settings",
      "/customers",
      "/customers/all",
      "/customers/my"
    ].includes(location.pathname);
  };

  const navClassName =
    location.pathname.includes("inventory") ||
    (location.pathname.includes("invoices") && hasAccessToInvoiceReport())
      ? "invoiceWrapper"
      : "contentWrapper";

  const internalUserCustomersMenuItems: SubMenuItemType[] = [
    {
      title: "Mina kunder",
      href: "/customers/my",
      notice: null,
      text: "Mina kunder"
    },
    {
      title: "Alla kunder",
      href: "/customers/all",
      notice: null,
      text: "Alla kunder"
    },
    ...(hasAccessToInvoiceReport()
      ? [
          {
            title: "Fakturering",
            href: "/customers/invoices",
            notice: null,
            text: "Fakturering"
          }
        ]
      : [])
  ];

  // When session expires, for now, we just want to close the Session-expires modal
  // and not redirect to logout, because we want to allow the user to copy information that they were working on.
  const handleSessionExpired = () => {
    setSessionExpired(false);
  };

  const handleLogout = () => {
    logoutUser();
    window.location.href = "/";
  };

  return (
    <div className="layoutWrapper">
      <Header />

      <div className="mainWrapper">
        {!isExternalUser && (
          <div className="menuWrapper internalUserMenu">
            <div className="menuInner">
              <Submenu
                className={"no-print"}
                menuItems={internalUserCustomersMenuItems}
              />
            </div>
          </div>
        )}

        {user.isAuthenticated && showCustomerMenu() && <CustomerSubmenu />}

        <div className={navClassName}>
          <Routes>
            <Route path="/customers" element={<Customers />}>
              <Route path="all" element={<AllCustomers />} />
              {hasAccessToInvoiceReport() && (
                <Route path="invoices" element={<Invoices />} />
              )}
              <Route path="my" element={<MyCustomers />} />
              <Route path=":customerId/dashboard" element={<Dashboard />} />
              <Route path=":customerId/inventory" element={<Inventory />} />
              <Route
                path=":customerId/delivery"
                element={<DocumentDelivery />}
              />
              <Route path=":customerId/inbox" element={<Inbox />} />
              <Route
                path=":customerId/settings"
                element={<CustomerSettings />}
              />
              <Route path=":customerId" element={<Customers />} />
              <Route index element={<Navigate to="my" replace />} />
            </Route>
            <Route path="/inbox" element={<Inbox />}>
              <Route
                path="sent"
                element={
                  <SentMessageList
                    messageList={sentMessages}
                    loadInbox={loadInbox}
                  />
                }
              />
              <Route path="newThread" element={<MessageEditor />} />
              <Route path="thread/:threadId" element={<Thread />} />
              <Route path="my" element={<MyThreads />} />
              <Route path="all" element={<AllThreads />} />
              <Route index element={<Navigate to="my" replace />} />
            </Route>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/customersettings" element={<CustomerSettings />} />
            <Route path="/settings" element={<CustomerServerSettings />} />
            <Route path="/delivery" element={<DocumentDelivery />}>
              <Route
                index
                element={
                  <Navigate to={!isExternalUser ? "new" : "upload"} replace />
                }
              />

              <Route path="upload" element={<DocumentDeliveryUpload />} />
              <Route path="uploaded" element={<DocumentDeliveryTable />} />
              <Route
                path="handled"
                element={
                  <DocumentDeliveryTableWithOptions
                    files={uploadedDocuments.filter((d) => d.handled)}
                    isLoading={isLoadingUploadedDocuments}
                    title="Hanterade"
                  />
                }
              />
              <Route
                path="new"
                element={
                  <DocumentDeliveryTableWithOptions
                    files={uploadedDocuments.filter((d) => !d.handled)}
                    isLoading={isLoadingUploadedDocuments}
                    title="Nya leveranser"
                  />
                }
              />
            </Route>
            <Route path="/archive" element={<DocumentArchive />} />
            <Route path="/wizard/:id" element={<Wizard />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route
              path="*"
              element={
                <Navigate to={isExternalUser ? "/dashboard" : "/customers"} />
              }
            />
          </Routes>
        </div>
      </div>

      <Footer />

      {sessionExpired && (
        <Modal
          size="xs"
          modalTitle="Session expired"
          onDismiss={handleSessionExpired}
        >
          <div className="modalText">
            <h5>
              Din session har gått ut. För att starta en ny session behöver du
              logga in på nytt.
            </h5>
            <div className="d-flex justify-content-end">
              <PrimaryButton size="md" onClick={handleLogout} type="submit">
                Logga ut
              </PrimaryButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
